import type { RealProfile } from '../types/profiles';
import type { RoundStats } from '../types/games';

export interface RealGameOverview {
  gameId: string;
  name: string;
  correct: number;
  incorrect: number;
  attempts: number;
  accuracyPct: number;
  avgResponseTimeMs: number;
}

export interface RealGameDetail extends RealGameOverview {
  rounds: RoundStats[];
  hardestRound: RoundStats | null;
  easiestRound: RoundStats | null;
}

export interface RealGamesAnalytics {
  overview: RealGameOverview[];
  byGame: Record<string, RealGameDetail>;
  mostAccurateGame: RealGameOverview | null;
  leastAccurateGame: RealGameOverview | null;
  fastestGame: RealGameOverview | null;
  slowestGame: RealGameOverview | null;
}

export function formatResponseTime(ms: number): string {
  if (!ms || ms <= 0) return '—';
  const seconds = ms / 1000;
  return seconds < 60 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds / 60)}min`;
}

export function computeRealGamesAnalytics(profiles: RealProfile[]): RealGamesAnalytics {
  const byGameId = new Map<string, { correct: number; errors: number; attempts: number; timeSpentMs: number }>();
  const roundsByGameId = new Map<string, Map<number, { correctCount: number; errorCount: number; timeSpentMs: number }>>();

  for (const profile of profiles) {
    for (const gm of profile.gameMetrics) {
      const totals = byGameId.get(gm.gameId) ?? { correct: 0, errors: 0, attempts: 0, timeSpentMs: 0 };
      totals.correct += gm.correct;
      totals.errors += gm.errors;
      totals.attempts += gm.attempts;
      totals.timeSpentMs += gm.timeSpentMs;
      byGameId.set(gm.gameId, totals);
    }
    for (const rm of profile.gameRoundMetrics) {
      const roundsMap = roundsByGameId.get(rm.gameId) ?? new Map();
      const round = roundsMap.get(rm.roundNumber) ?? { correctCount: 0, errorCount: 0, timeSpentMs: 0 };
      round.correctCount += rm.correctCount;
      round.errorCount += rm.errorCount;
      round.timeSpentMs += rm.timeSpentMs;
      roundsMap.set(rm.roundNumber, round);
      roundsByGameId.set(rm.gameId, roundsMap);
    }
  }

  const overview: RealGameOverview[] = [...byGameId.entries()].map(([gameId, totals]) => {
    const total = totals.correct + totals.errors;
    return {
      gameId,
      name: gameId,
      correct: totals.correct,
      incorrect: totals.errors,
      attempts: totals.attempts,
      accuracyPct: total > 0 ? Math.round((totals.correct / total) * 100) : 0,
      avgResponseTimeMs: totals.attempts > 0 ? totals.timeSpentMs / totals.attempts : 0,
    };
  });

  const byGame: Record<string, RealGameDetail> = {};
  for (const game of overview) {
    const roundsMap = roundsByGameId.get(game.gameId) ?? new Map();
    const rounds: RoundStats[] = [...roundsMap.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([roundNumber, r]) => {
        const total = r.correctCount + r.errorCount;
        return {
          roundId: `${game.gameId}_r${roundNumber}`,
          order: roundNumber,
          prompt: `Rodada ${roundNumber}`,
          correct: r.correctCount,
          incorrect: r.errorCount,
          accuracyPct: total > 0 ? Math.round((r.correctCount / total) * 100) : 0,
          avgResponseTimeMs: total > 0 ? r.timeSpentMs / total : 0,
        };
      });
    const withAttempts = rounds.filter((r) => r.correct + r.incorrect > 0);
    const hardestRound = withAttempts.length
      ? [...withAttempts].sort((a, b) => a.accuracyPct - b.accuracyPct)[0]
      : null;
    const easiestRound = withAttempts.length
      ? [...withAttempts].sort((a, b) => b.accuracyPct - a.accuracyPct)[0]
      : null;
    byGame[game.gameId] = { ...game, rounds, hardestRound, easiestRound };
  }

  const sortedByAccuracy = [...overview].sort((a, b) => b.accuracyPct - a.accuracyPct);
  const sortedBySpeed = [...overview].filter((g) => g.avgResponseTimeMs > 0).sort((a, b) => a.avgResponseTimeMs - b.avgResponseTimeMs);

  return {
    overview,
    byGame,
    mostAccurateGame: sortedByAccuracy[0] ?? null,
    leastAccurateGame: sortedByAccuracy[sortedByAccuracy.length - 1] ?? null,
    fastestGame: sortedBySpeed[0] ?? null,
    slowestGame: sortedBySpeed[sortedBySpeed.length - 1] ?? null,
  };
}
